import { StatusType } from "./characters";

export type Status<T extends StatusType> = {
  type: T;
  duration: number;
  magnitude?: number;
  payload?: any;
};

//WeakStatus is removed at status update (instead increasing duration)
export type WeakStatus<T extends StatusType> = Status<T> & {
  payload: { weak: true };
};

export type DmgStatus<T extends StatusType> = Status<T> & {
  payload: { dmg: number };
};
export type MagnitudeStatus<T extends StatusType> = Status<T> & {
  payload: { magnitude: number };
};

export const isDmgStatus = <T extends StatusType>(
  s: Status<T>
): s is DmgStatus<T> => {
  return (s as DmgStatus<T>).payload?.dmg !== undefined;
};

export class StatusHandler {
  static createStatusFilter =
    <T extends StatusType>(type: T) =>
    (status: Status<StatusType>): status is Status<T> => {
      return status.type === type;
    };

  static findStatus = <T extends StatusType>(
    type: T,
    statusList: Status<StatusType>[]
  ): Status<T> | undefined => {
    return statusList.find(this.createStatusFilter(type));
  };

  static createStatus = <T extends StatusType>(
    type: T,
    payload?: any
  ): Status<T> => {
    return {
      type,
      duration: 0,
      payload,
    };
  };

  static createDmgStatus = <T extends StatusType>(
    type: T,
    payload: any & {
      dmg: number;
    }
  ): DmgStatus<T> => {
    return {
      type,
      duration: 0,
      payload,
    };
  };

  static addDmgStatus = <T extends StatusType, U extends StatusType>(
    status: DmgStatus<T>,
    statusList: Status<T | U>[]
  ): Status<T | U>[] => {
    const oldStatus: DmgStatus<T | U> | undefined = statusList
      .filter(isDmgStatus) //@TODO: find better workaround => type guard does not take "&& isDmgStatus(s)" in the find call
      .find((s) => s.type === status.type);

    if (!oldStatus) {
      return [...statusList, status];
    }

    return [
      ...statusList.filter((s) => s.type !== status.type),
      {
        ...oldStatus,
        payload: {
          ...status.payload,
          dmg: oldStatus.payload.dmg + status.payload.dmg,
        },
      },
    ];
  };
}
